import type { ApiResponse } from '@vibe/shared-types'
import { getCurrentIdToken } from '@/stores/auth-store'

/**
 * 클라이언트 → 백엔드 호출 래퍼.
 *
 * 인증 정책:
 *   - production: Firebase ID Token (FR-01에서 Auth 클라이언트 연결 후 채움)
 *   - development: `X-Vibe-Dev-User` 헤더로 백엔드 바이패스 — Firebase Auth UI가 완성되기 전까지 사용
 *
 * dev uid 는 localStorage 에 영속 보관해 같은 학생으로 재시작해도 유지.
 */

export const API_URL = (import.meta.env.VITE_API_URL ?? 'http://127.0.0.1:8787').replace(/\/$/, '')

const DEV_UID_KEY = 'vibe:dev-uid'
const DEV_PLAN_KEY = 'vibe:dev-plan'

export function getDevUid(): string {
  let uid = localStorage.getItem(DEV_UID_KEY)
  if (!uid) {
    uid = 'dev-' + Math.random().toString(36).slice(2, 10)
    localStorage.setItem(DEV_UID_KEY, uid)
  }
  return uid
}

export function getDevPlan(): 'free' | 'basic' | 'premium' {
  const v = localStorage.getItem(DEV_PLAN_KEY)
  if (v === 'free' || v === 'basic' || v === 'premium') return v
  return 'premium'
}

export function getAuthHeaders(): Record<string, string> {
  // Firebase ID Token이 있으면 우선. 학생이 로그인한 production 흐름.
  const idToken = getCurrentIdToken()
  if (idToken) {
    return { Authorization: `Bearer ${idToken}` }
  }
  // dev 모드에서 Firebase 로그인 전에 백엔드 호출이 필요한 경우 바이패스.
  if (import.meta.env.DEV) {
    return {
      'X-Vibe-Dev-User': getDevUid(),
      'X-Vibe-Dev-Plan': getDevPlan(),
    }
  }
  // production이지만 토큰 없음 → 백엔드가 401로 응답하게 둔다 (로그인 화면으로 리다이렉트).
  return {}
}

export class ApiClientError extends Error {
  status: number
  code: string
  detail?: unknown
  constructor(message: string, init: { status: number; code: string; detail?: unknown }) {
    super(message)
    this.name = 'ApiClientError'
    this.status = init.status
    this.code = init.code
    if (init.detail !== undefined) this.detail = init.detail
  }
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit & { skipAuth?: boolean } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...((init.headers as Record<string, string>) ?? {}),
  }
  if (!init.skipAuth) Object.assign(headers, getAuthHeaders())

  const res = await fetch(`${API_URL}${path}`, { ...init, headers })
  const body = (await res.json().catch(() => null)) as ApiResponse<T> | null

  if (!res.ok || !body || !body.ok) {
    const code = body && !body.ok ? body.error.code : 'HTTP_ERROR'
    const message =
      body && !body.ok ? body.error.message : `요청 실패 (HTTP ${res.status})`
    const detail = body && !body.ok ? body.error.detail : undefined
    if (res.status === 401 || res.status >= 500) {
      // 401/5xx 는 콘솔에 detail 까지 그대로 노출 — 진단을 위해.
      console.error('[apiFetch]', path, { status: res.status, code, message, detail })
    }
    throw new ApiClientError(message, { status: res.status, code, detail })
  }
  return body.data
}
